import React, { useCallback, useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import ImageResize from "tiptap-extension-resize-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import Placeholder from "@tiptap/extension-placeholder";
import { TextAlign } from "@tiptap/extension-text-align";
import { Color } from "@tiptap/extension-color";
import LinkExtension from "@tiptap/extension-link";
import TextStyle from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";

// Icons
import {
  AlignCenter, AlignLeft, AlignRight, Baseline, Bold, Heading1, Heading2, Heading3,
  Highlighter, ImageIcon, Italic, Link as LinkIcon, List, ListOrdered, 
  Table as LucideTable, Quote, Check, X, Undo, Redo
} from "lucide-react";

import TableBubbleMenu from "./TableBubbleMenu";
import "../../assets/css/EditorStyle.css"; // Aapki CSS file

// --- HELPER COMPONENT FOR TOOLBAR BUTTONS ---
// Ye button style same rakhega aur Focus loss prevent karega
const MenuBtn = ({ onClick, isActive, children, title }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()} // ⚡ IMPORTANT: Focus bachane ke liye
    onClick={onClick}
    title={title}
    className={`editor-menu-btn ${isActive ? "is-active" : ""}`}
    style={{
      background: isActive ? "#e0efff" : "transparent",
      color: isActive ? "#1a73e8" : "#444",
      border: "none",
      borderRadius: "6px",
      padding: "6px 8px",
      margin: "0 2px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}
  >
    {children}
  </button>
);

const Divider = () => (
  <div style={{ width: "1px", height: "24px", background: "#ddd", margin: "0 8px" }} />
);

const BlogTextEditor = ({ onChange, value, placeholder }) => {
  const [isLinkInputVisible, setIsLinkInputVisible] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Image,
      ImageResize,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({
        placeholder: placeholder || "Write something amazing...",
        emptyEditorClass: "is-editor-empty",
      }),
      TextAlign.configure({
        types: ["heading", "paragraph", "tableCell", "tableHeader"],
      }),
      TextStyle,
      Color,
      LinkExtension.configure({
        openOnClick: false,
        autolink: true,
      }),
      Highlight.configure({ multicolor: true }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        // Bootstrap classes for spacing + custom style
        class: "form-control border-0 focus-ring-0", 
        style: "min-height: 400px; padding: 20px; outline: none;"
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync Content
  useEffect(() => {
    if (editor && value && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // --- IMAGE UPLOAD (BASE64) ---
  // Note: Hum yahan Base64 insert karenge. S3 upload 'CreateNewFeed' page karega submit par.
  const addImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = (event) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
           if (typeof reader.result === 'string') {
             editor?.chain().focus().setImage({ src: reader.result }).run();
           }
        };
      }
    };
    input.click();
  }, [editor]);

  // --- LINK HANDLERS ---
  const openLinkModal = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    setLinkUrl(previousUrl || "");
    setIsLinkInputVisible(true);
  }, [editor]);

  const saveLink = useCallback(() => {
    if (linkUrl) {
      editor?.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    } else {
      editor?.chain().focus().unsetLink().run();
    }
    setIsLinkInputVisible(false);
    setLinkUrl("");
  }, [editor, linkUrl]);

  if (!editor) return null;

  return (
    <div 
      className="editor-wrapper" 
      style={{ 
        border: "1px solid #ddd", 
        borderRadius: "12px", 
        overflow: "hidden", 
        background: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
      }}
    >
      
      {/* --- TOOLBAR --- */}
      <div 
        className="editor-toolbar d-flex flex-wrap align-items-center p-2"
        style={{ 
          background: "#f8f9fa", 
          borderBottom: "1px solid #ddd",
          position: "sticky",
          top: 0,
          zIndex: 10
        }}
      >
        
        {/* Headings */}
        <MenuBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive("heading", { level: 1 })} title="Heading 1">
          <Heading1 size={18} />
        </MenuBtn>
        <MenuBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive("heading", { level: 2 })} title="Heading 2">
          <Heading2 size={18} />
        </MenuBtn>
        <MenuBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive("heading", { level: 3 })} title="Heading 3">
          <Heading3 size={18} />
        </MenuBtn>

        <Divider />

        {/* Text Formats */}
        <MenuBtn onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} title="Bold">
          <Bold size={18} />
        </MenuBtn>
        <MenuBtn onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} title="Italic">
          <Italic size={18} />
        </MenuBtn>

        {/* Colors (Custom Wrapper) */}
        <div className="position-relative d-flex align-items-center mx-1" title="Text Color">
          <Baseline size={18} style={{ color: editor.getAttributes("textStyle").color || "#444" }} />
          <input
            type="color"
            style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
            onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
            value={editor.getAttributes("textStyle").color || "#000000"}
          />
        </div>

        <div className="position-relative d-flex align-items-center mx-1" title="Highlight">
          <Highlighter size={18} style={{ color: "#444" }} />
          <div style={{ position: 'absolute', bottom: 0, right: 0, width: 8, height: 8, borderRadius: '50%', background: editor.getAttributes("highlight").color || 'transparent', border: '1px solid #ccc' }}></div>
          <input
            type="color"
            style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
            onInput={(e) => editor.chain().focus().toggleHighlight({ color: e.target.value }).run()}
            value={editor.getAttributes("highlight").color || "#ffff00"}
          />
        </div>

        <Divider />

        {/* Lists */}
        <MenuBtn onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} title="Bullet List">
          <List size={18} />
        </MenuBtn>
        <MenuBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} title="Ordered List">
          <ListOrdered size={18} />
        </MenuBtn>

        <Divider />

        {/* Alignments */}
        <MenuBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} isActive={editor.isActive({ textAlign: "left" })} title="Align Left">
          <AlignLeft size={18} />
        </MenuBtn>
        <MenuBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} isActive={editor.isActive({ textAlign: "center" })} title="Align Center">
          <AlignCenter size={18} />
        </MenuBtn>
        <MenuBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} isActive={editor.isActive({ textAlign: "right" })} title="Align Right">
          <AlignRight size={18} />
        </MenuBtn>

        <Divider />

        {/* Inserts */}
        <MenuBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive("blockquote")} title="Quote">
          <Quote size={18} />
        </MenuBtn>

        <MenuBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert Table">
          <LucideTable size={18} />
        </MenuBtn>

        <MenuBtn onClick={openLinkModal} isActive={editor.isActive("link")} title="Add Link">
          <LinkIcon size={18} />
        </MenuBtn>

        <MenuBtn onClick={addImage} title="Upload Image">
          <ImageIcon size={18} />
        </MenuBtn>

        <Divider />
        
         {/* History */}
         <MenuBtn onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo size={18} />
        </MenuBtn>
        <MenuBtn onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo size={18} />
        </MenuBtn>

      </div>

      {/* --- EXTRA MENUS --- */}
      {editor && <TableBubbleMenu editor={editor} />}

      {/* --- EDITOR AREA --- */}
      <div className="position-relative">
        <EditorContent editor={editor} />

        {/* Link Modal (Styled) */}
        {isLinkInputVisible && (
          <div 
            className="position-absolute bg-white shadow rounded p-2 d-flex gap-2 align-items-center"
            style={{ top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 100, border: '1px solid #ddd' }}
          >
            <input
              type="text"
              className="form-control form-control-sm"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              autoFocus
              style={{ width: '200px' }}
            />
            <button onClick={saveLink} className="btn btn-sm btn-success p-1"><Check size={14} /></button>
            <button onClick={() => setIsLinkInputVisible(false)} className="btn btn-sm btn-danger p-1"><X size={14} /></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogTextEditor;
